import * as THREE from 'three';
import { tagStroke, truncateLabel } from '../../utils/neuralSceneUtils';
import { projectNebulaTitleBoxes } from '../neuralMaterials/clusterNebula/placeTags';
import {
  collectNodeDiskScreens,
  collectVisibleLabelBoxes,
  pickHoverNameSeat,
  setLabelCameraBasis,
} from './labelSlots';

/** CSS2D uses 2D translate(), so Chromium snaps labels to whole pixels on a slow orbit. */
function softenCss2dPixels(root) {
  const kids = root?.children;
  if (!kids) return;
  for (let i = 0; i < kids.length; i += 1) {
    const t = kids[i].style.transform;
    if (!t || t.includes('translate3d')) continue;
    kids[i].style.transform = `${t.replace(
      /translate\(([-\d.eE+]+)px,\s*([-\d.eE+]+)px\)/g,
      'translate3d($1px, $2px, 0)',
    )} rotate(0.001deg)`;
  }
}

const NODE_CORE_RADIUS = 0.155;
const HOVER_RING_MID = 0.78;
const HOVER_RING_PAD = 1.16;
const HOVER_WHITE = new THREE.Color(0xffffff);

export function createFrameOverlays({
  camera,
  meshByTaskId,
  selectionRing,
  hoverRing,
  hoverNameEl,
  hoverNameText,
  labelByTaskId,
  selectedTaskIdLocal,
  hoverPointerId,
  nebula,
  distScale,
  floatingCardWrapRef,
  container,
  raycaster,
  labelRenderer,
  composer,
  renderer,
  scene,
  delta,
}) {
  const { camDir, toPointFromCam, projectForCard } = raycaster.vectors;

  const updateSelectionRing = () => {
    if (!selectionRing.visible) return;
    const ringTarget = meshByTaskId.get(selectedTaskIdLocal);
    if (ringTarget && ringTarget.visible) {
      selectionRing.position.copy(ringTarget.position);
      selectionRing.quaternion.copy(camera.quaternion);
      const ringFade = THREE.MathUtils.clamp(
        ringTarget.userData.visT / Math.max(ringTarget.userData.baseOpacity, 0.001),
        0,
        1,
      );
      selectionRing.scale.setScalar(distScale * 1.14 * (ringTarget.userData.nodeMeta?.size ?? 1) * ringFade);
    } else {
      selectionRing.visible = false;
    }
  };

  const hideHoverName = () => {
    if (hoverNameEl) hoverNameEl.style.opacity = '0';
  };

  const updateHoverChrome = () => {
    if (!hoverPointerId || hoverPointerId === selectedTaskIdLocal) {
      if (hoverRing) hoverRing.visible = false;
      hideHoverName();
      return;
    }
    const target = meshByTaskId.get(hoverPointerId);
    if (!target?.visible || target.userData.exiting || (target.userData.visT ?? 0) < 0.08) {
      if (hoverRing) hoverRing.visible = false;
      hideHoverName();
      return;
    }
    const ud = target.userData;
    const layout = ud.layoutScale > 0 ? ud.layoutScale : Math.max(target.scale.x, 0.001);

    if (hoverRing) {
      hoverRing.visible = true;
      hoverRing.position.copy(target.position);
      hoverRing.quaternion.copy(camera.quaternion);
      hoverRing.scale.setScalar((NODE_CORE_RADIUS * HOVER_RING_PAD * layout) / HOVER_RING_MID);
      hoverRing.material.color.copy(ud.baseColor || HOVER_WHITE).lerp(HOVER_WHITE, 0.55);
      hoverRing.material.opacity = 0.95 * THREE.MathUtils.clamp(
        ud.visT / Math.max(ud.baseOpacity, 0.001),
        0,
        1,
      );
    }

    if (!hoverNameEl || !hoverNameText) return;
    const existing = labelByTaskId?.get(hoverPointerId);
    if (existing?.label?.parent && parseFloat(existing.element.style.opacity) > 0.12) {
      hideHoverName();
      return;
    }
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    projectForCard.copy(target.position).project(camera);
    if (projectForCard.z > 1) {
      hideHoverName();
      return;
    }
    const nx = (projectForCard.x * 0.5 + 0.5) * cw;
    const ny = (-projectForCard.y * 0.5 + 0.5) * ch;
    const next = truncateLabel(ud.nodeMeta?.name, 56);
    if (hoverNameText.textContent !== next) hoverNameText.textContent = next;
    const lw = hoverNameText.offsetWidth || Math.min(14 + next.length * 6.2, 180);
    const lh = hoverNameText.offsetHeight || 20;
    setLabelCameraBasis(camera);
    const labels = collectVisibleLabelBoxes(labelByTaskId, camera, container, meshByTaskId);
    const titles = nebula?.visible ? projectNebulaTitleBoxes(nebula, camera, container) : [];
    const disks = collectNodeDiskScreens(meshByTaskId, camera, cw, ch);
    if (hoverNameEl._hid !== hoverPointerId) {
      hoverNameEl._hx = hoverNameEl._hy = null;
      hoverNameEl._hid = hoverPointerId;
    }
    const seat = pickHoverNameSeat(
      nx, ny, lw, lh, cw, ch, labels, titles, disks, hoverPointerId,
      hoverNameEl._hx != null ? { left: hoverNameEl._hx, top: hoverNameEl._hy } : null,
    );
    hoverNameEl._hx = seat.left;
    hoverNameEl._hy = seat.top;
    hoverNameText.style.borderColor = tagStroke(ud.baseColor, 0.7);
    hoverNameEl.style.transform = `translate3d(${seat.left}px, ${seat.top}px, 0)`;
    hoverNameEl.style.opacity = '1';
  };

  const updateFloatingCard = () => {
    const fc = floatingCardWrapRef.current;
    if (!fc) return;
    const tid = selectedTaskIdLocal;
    const subMesh = meshByTaskId.get(tid);
    if (!tid || !subMesh?.visible || subMesh.userData.exiting || subMesh.userData.visT < 0.08) {
      fc.style.visibility = 'hidden';
      fc.style.pointerEvents = 'none';
      fc._cardX = fc._cardY = null;
      return;
    }
    const wpos = subMesh.position;
    toPointFromCam.copy(wpos).sub(camera.position);
    camera.getWorldDirection(camDir);
    if (toPointFromCam.dot(camDir) <= 0.08) {
      fc.style.visibility = 'hidden';
      fc.style.pointerEvents = 'none';
      fc._cardX = fc._cardY = null;
      return;
    }
    projectForCard.copy(wpos).project(camera);
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const px = (projectForCard.x * 0.5 + 0.5) * cw;
    const py = (-projectForCard.y * 0.5 + 0.5) * ch;
    const gap = 28;
    const cardW = fc.offsetWidth || 300;
    const cardH = fc.offsetHeight || 240;
    const portrait = cw < 640 || cw / ch < 0.85;
    let left = portrait ? (cw - cardW) / 2 : px + gap;
    let top = portrait ? ch - cardH - 20 : py - cardH * 0.2;
    if (!portrait && left + cardW > cw - 12) left = px - gap - cardW;
    const maxLeft = Math.max(12, cw - cardW - 12);
    const maxTop = Math.max(12, ch - cardH - 12);
    left = THREE.MathUtils.clamp(left, 12, maxLeft);
    top = THREE.MathUtils.clamp(top, 12, maxTop);
    const jumping = !!subMesh.userData.layoutJump;
    const ease = 1 - Math.pow(jumping ? 0.38 : 0.72, Math.max(delta || 1 / 60, 0.001) * 60);
    if (fc._cardX == null) {
      fc._cardX = left;
      fc._cardY = top;
    } else {
      fc._cardX += (left - fc._cardX) * ease;
      fc._cardY += (top - fc._cardY) * ease;
    }
    fc.style.visibility = 'visible';
    fc.style.pointerEvents = 'auto';
    fc.style.transform = `translate3d(${fc._cardX}px, ${fc._cardY}px, 0)`;
  };

  const paintFloatingCardFrost = () => {
    const fc = floatingCardWrapRef.current;
    const frost = fc?.querySelector?.('.neural-view__floating-card-frost');
    const src = renderer.domElement;
    if (!fc || !frost || !src) return;
    if (fc.style.visibility === 'hidden') return;
    const cssW = src.clientWidth || src.width;
    const cssH = src.clientHeight || src.height;
    if (cssW < 2 || cssH < 2) return;
    const pad = 24;
    const cardW = fc.offsetWidth || 300;
    const cardH = fc.offsetHeight || 240;
    const x = fc._cardX ?? 0;
    const y = fc._cardY ?? 0;
    const fw = Math.max(1, Math.round((cardW + pad * 2) * 0.5));
    const fh = Math.max(1, Math.round((cardH + pad * 2) * 0.5));
    if (frost.width !== fw || frost.height !== fh) {
      frost.width = fw;
      frost.height = fh;
    }
    frost.style.width = `${cardW + pad * 2}px`;
    frost.style.height = `${cardH + pad * 2}px`;
    frost.style.left = `${-pad}px`;
    frost.style.top = `${-pad}px`;
    frost.style.transform = 'none';
    const sx = src.width / cssW;
    const sy = src.height / cssH;
    const ctx = frost._ctx || (frost._ctx = frost.getContext('2d'));
    ctx.drawImage(
      src,
      (x - pad) * sx,
      (y - pad) * sy,
      (cardW + pad * 2) * sx,
      (cardH + pad * 2) * sy,
      0,
      0,
      fw,
      fh,
    );
  };

  const paint = () => {
    if (labelRenderer.domElement.style.visibility === 'hidden') {
      labelRenderer.domElement.style.visibility = '';
    }
    if (composer) composer.render(delta);
    else renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
    softenCss2dPixels(labelRenderer.domElement);
    paintFloatingCardFrost();
  };

  return { updateSelectionRing, updateHoverChrome, updateFloatingCard, paint };
}
